import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Typography } from '@mui/material';
import { alpha, getContrastRatio } from '@mui/material/styles';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PinwheelBackground from './PinwheelBackground';
import { marketingDarkPalette } from '../../../theme_landing_page_2';

const chips = [
  { label: 'Gas safety · expires in 14 days', color: 'warning.main' },
  { label: 'Compliance agent · flow started', color: 'secondary.main' },
  { label: 'Tenant · Flat 2 confirmed', color: 'success.main' },
  { label: 'Engineer booked · Saturday', color: 'primary.main' },
  { label: 'Licence · renewed', color: 'success.main' },
  { label: 'Invoice · received', color: 'primary.main' },
  { label: 'MTD quarterly report · prepared', color: 'secondary.main' },
];
const chipSlots = [
  { top: 40, left: 0 },
  { top: 80, right: 0 },
  { bottom: 0, left: 32 },
  { bottom: 24, right: 0 },
];
const trustPoints = ['No credit card needed', 'Setup in 2 minutes', 'Cancel any time'];

export const HERO_NOTIFICATION_TIMING = { holdSeconds: 4, transitionSeconds: 0.4, staggerSeconds: 2 };
const notificationCycle = chips.length * HERO_NOTIFICATION_TIMING.staggerSeconds;
const notificationPercent = (seconds: number) => `${seconds / notificationCycle * 100}%`;
const notificationArrival = HERO_NOTIFICATION_TIMING.transitionSeconds;
const notificationDismissal = notificationArrival + HERO_NOTIFICATION_TIMING.holdSeconds;
const notificationEnd = notificationDismissal + HERO_NOTIFICATION_TIMING.transitionSeconds;

const MillosHero: React.FC = () => (
  <Box component="section" id="top" aria-labelledby="hero-heading"
    sx={{
      position: 'relative', isolation: 'isolate', overflow: 'hidden',
      '--hero-offset-y': '-26px',
      bgcolor: 'background.default', color: 'text.primary',
      height: '100svh', minHeight: 640, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', px: 3, pt: 10, pb: 8,
      '@media (max-width:639px)': {
        height: 'auto', minHeight: '100svh', boxSizing: 'border-box',
        display: 'grid', gridTemplateRows: 'minmax(168px, 1fr) auto minmax(168px, 1fr)',
        gap: 0, pt: 'max(24px, env(safe-area-inset-bottom))',
        pb: 'max(24px, env(safe-area-inset-bottom))',
      },
    }}>
    <PinwheelBackground />
    <Box aria-hidden="true" sx={{
      position: 'absolute', top: 'calc(50% + var(--hero-offset-y))', left: '50%',
      width: 'min(800px, calc(100% - 72px))', height: 450,
      transform: 'translate(-50%, -50%)', zIndex: 1, pointerEvents: 'none',
      '@keyframes millosHeroNotification': {
        '0%': { opacity: 0, transform: 'translate(0, 12px)' },
        [`${notificationPercent(notificationArrival)}, ${notificationPercent(notificationDismissal)}`]: { opacity: 1, transform: 'translate(0, 0)' },
        [`${notificationPercent(notificationEnd)}, 100%`]: { opacity: 0, transform: 'translate(var(--chip-dismiss-x), -4px)' },
      },
      '@media (max-width:639px)': { display: 'none' },
    }}>
      {chips.map((chip, index) => (
        <Box key={chip.label} sx={(theme) => ({
          position: 'absolute', ...chipSlots[index % chipSlots.length],
          '--chip-dismiss-x': index % 2 === 0 ? '-64px' : '64px',
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
          bgcolor: 'background.paper', border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'divider' : theme.palette.tokens.hairline,
          borderRadius: `${Number(theme.shape.borderRadius) * 0.75}px`,
          color: 'text.secondary', fontFamily: theme.typography.code.fontFamily,
          fontSize: 11, lineHeight: 1.4, whiteSpace: 'nowrap',
          opacity: 0, scale: '0.95',
          animation: `millosHeroNotification ${notificationCycle}s ease-in-out ${index * HERO_NOTIFICATION_TIMING.staggerSeconds}s infinite both`,
          '@media (prefers-reduced-motion:reduce)': {
            animation: 'none', opacity: 1, ...(index >= chipSlots.length ? { display: 'none' } : {}),
          },
          ...(index % chipSlots.length > 1 ? { '@media (max-width:899px)': { display: 'none' } } : {}),
        })}>
          <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: chip.color }} />
          {chip.label}
        </Box>
      ))}
    </Box>
    <Box sx={(theme) => ({
      position: 'absolute', top: 'calc(50% - 40px + var(--hero-offset-y))', transform: 'translateY(-0.525em)',
      fontSize: 'clamp(3.25rem, 5.85vw, 4.55rem)',
      zIndex: 2, textAlign: 'center', width: 'calc(100% - 48px)', maxWidth: 1215.5,
      '@media (max-width:639px)': {
        position: 'relative', top: 'auto', transform: 'none', width: '100%',
        gridRow: 2, my: 0, py: 3,
      },
      '&::before': {
        content: '""', position: 'absolute', zIndex: -1, inset: '-12px -16px',
        background: `radial-gradient(ellipse, ${alpha(theme.palette.background.default, 0.86)} 0%, ${alpha(theme.palette.background.default, 0.6)} 38%, transparent 70%)`,
        pointerEvents: 'none',
      },
    })}>
      <Typography component="h1" variant="displayLg" id="hero-heading" sx={(theme) => ({
        m: 0, fontSize: 'clamp(3.575rem, 6.435vw, 5.005rem)', whiteSpace: 'normal',
        position: 'relative', isolation: 'isolate',
        '&::before': {
          content: '""', position: 'absolute', zIndex: -1, inset: '-12px -16px',
          background: `radial-gradient(ellipse, ${theme.palette.background.default} 20%, ${alpha(theme.palette.background.default, 0.85)} 50%, transparent 75%)`,
          pointerEvents: 'none',
        },
        '@media (max-width:639px)': {
          fontSize: 'clamp(2.5rem, 10.5vw, 3.5rem)',
          whiteSpace: 'normal', maxWidth: 360, mx: 'auto',
        },
      })}>
        Keep every property <Box component="em">compliant.</Box>
      </Typography>
      <Typography component="p" variant="bodySm" sx={(theme) => ({
        maxWidth: 598, mx: 'auto', mt: 2, mb: 3, color: 'text.secondary',
        fontSize: `calc(${typeof theme.typography.bodySm.fontSize === 'number'
          ? `${theme.typography.bodySm.fontSize}px` : theme.typography.bodySm.fontSize} * 1.3)`,
        '@media (max-width:639px)': { fontSize: 16, maxWidth: 320 },
      })}>
        Connect your property data. Automate your workflows.
      </Typography>
      <Box sx={{
        display: 'flex', gap: '10px', justifyContent: 'center',
        '@media (max-width:639px)': { flexDirection: 'column', alignItems: 'center', maxWidth: 360, mx: 'auto' },
      }}>
        <Button component={RouterLink} to="/register" variant="contained" size="large"
          sx={(theme) => ({
            bgcolor: getContrastRatio(theme.palette.primary.main, theme.palette.primary.contrastText) >= 4.5
              ? 'primary.main' : 'primary.dark',
            '&:hover': { bgcolor: 'primary.dark' },
          })}>Start free</Button>
        <Button component={RouterLink} to="/how-it-works#renewal-flow" variant="outlined" size="large"
          endIcon={<PlayArrowOutlinedIcon />} sx={{
            color: 'text.primary', borderColor: 'divider', bgcolor: 'background.paper',
            '@media (max-width:639px)': { display: 'none' },
          }}>
          Discover our AI
        </Button>
      </Box>
    </Box>
    <Box sx={{
      position: 'absolute', bottom: 24, left: 24, right: 24, zIndex: 2,
      textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
      '@media (max-width:639px)': {
        position: 'relative', bottom: 'auto', left: 'auto', right: 'auto',
        gridRow: 3, alignSelf: 'end', mt: 0, width: '100%', flexShrink: 0,
      },
    }}>
      <Box sx={(theme) => ({
        position: 'relative', isolation: 'isolate', maxWidth: '100%',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        '&::before': {
          content: '""', position: 'absolute', zIndex: -1, inset: '-10px -16px',
          background: `radial-gradient(ellipse, ${theme.palette.background.default} 45%, ${alpha(theme.palette.background.default, 0.85)} 70%, transparent 100%)`,
          pointerEvents: 'none',
        },
      })}>
        <Typography component="p" variant="captionUppercase" sx={{ m: 0, color: 'text.secondary' }}>
          Built for
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, maxWidth: '100%' }}>
          <Chip component={RouterLink} to="/for/portfolio-landlords" label="Landlords" variant="outlined" clickable />
          <Chip component={RouterLink} to="/for/letting-agents" label="Letting agents" variant="outlined" clickable />
        </Box>
      </Box>
      <Box component="ul" aria-label="Trial benefits" sx={{
        listStyle: 'none', m: 0, mt: 1, px: 2.5, py: 1.25, width: '100%', maxWidth: 800,
        boxSizing: 'border-box', bgcolor: marketingDarkPalette.surface, color: marketingDarkPalette.text,
        borderRadius: 3, display: 'flex', justifyContent: 'space-around', gap: 1.5, flexWrap: 'wrap',
        '@media (max-width:639px)': { display: 'none' },
      }}>
        {trustPoints.map((point) => (
          <Box component="li" key={point} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 500 }}>
            <Box component="span" aria-hidden="true" sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: marketingDarkPalette.accent, color: marketingDarkPalette.surface, display: 'grid', placeItems: 'center', fontSize: 10 }}>✓</Box>
            {point}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export default MillosHero;
